/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
    Card,
    CardContent,
    CardGroup,
    CardHeader,
    Icon, Placeholder, PlaceholderImage,
    Statistic,
    StatisticLabel,
    StatisticValue,
} from 'semantic-ui-react'
import { NavLink } from 'react-router-dom-v5-compat'
import { useTranslation } from 'react-i18next'
import useBreadcrumb from '../NavBar/useBreadcrumb'
import React, { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getPausedStores } from '../../services/Metrics/Metrics.service'
import moment from 'moment'
import { OpenRateRadialChart } from './OpeningTimes/OpenRateRadialChart'
import { useOverallOpeningTimes } from './OpeningTimes/hooks'

const cardStyle = css`
    &.ui.card.card {
        border: 1px solid rgba(34, 36, 38, 0.15);
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
        height: 300px;
    }

    &.ui.card.card:hover {
        transform: translateY(-8px);
        border: 1px solid rgba(34, 36, 38, 0.15);
        box-shadow: 0 10px 15px -3px rgba(0, 128, 128, 0.2), 0 4px 6px -2px rgba(0, 128, 128, 0.1);

        .icon {
            transform: scale(1.1);
        }
    }

    .content {
        text-align: center;
        padding: 2.5rem 1.5rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    }

    .icon {
        margin-bottom: 1.5rem;
        transition: all 0.3s ease;
    }

    .header {
        color: #2c3e50 !important;
        font-size: 1.3rem !important;
        font-weight: 600 !important;
        line-height: 1.4 !important;
        margin-top: 0.5rem !important;
    }
`

const cardGroupStyle = css`
    &.ui.cards {
        margin-top: 2rem;
    }
`

const OpeningActivity = () => {
    const [t] = useTranslation()
    const {setPaths} = useBreadcrumb()

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('home.submenus.paused'),
            link: false,
        }])
    }, [setPaths, t])

    const from = moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    const to = moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    const {data: overallOpeningTimes} = useOverallOpeningTimes({ from, to })

    const {data: pausedStores} = useQuery({
        queryFn: getPausedStores,
        queryKey: ['metrics', 'paused-stores'],
    })

    return (
      <CardGroup centered css={cardGroupStyle}>
          <Card
            as={NavLink}
            to={'/analytics/openings/opening-times'}
            css={cardStyle}
          >
              <CardContent>
                  {overallOpeningTimes ?
                    <div
                      css={css`
                          padding-top: 2rem;
                      `}>
                        <OpenRateRadialChart rate={overallOpeningTimes?.averageOpenRate || 0} animationsEnabled/>
                    </div> :
                    <div
                      css={css`
                          display: flex;
                          justify-content: center;
                          align-items: center;
                          width: 260px;
                          height: 166px;
                      `}>
                        <Placeholder style={{height: 134, width: 140}}>
                            <PlaceholderImage/>
                        </Placeholder>
                    </div>
                  }
              </CardContent>

              <CardContent extra>
                  <div
                    css={css`
                        display: flex;
                        justify-content: center;
                        align-items: flex-end;
                    `}>
                      <Icon size="big" color="teal" name="chart bar outline"/>
                      <CardHeader>{t('openings.analyse_openings_stats')}</CardHeader>
                  </div>
              </CardContent>
          </Card>

          <Card
            as={NavLink}
            to={'/analytics/openings/current-openings'}
            css={cardStyle}
          >
              <CardContent>
                  <Statistic>
                      <StatisticValue>
                          {pausedStores ?
                            pausedStores.merchants.filter(pos => pos.isClosed === false).length :
                            '-'
                          }
                      </StatisticValue>
                      <StatisticLabel>{t('openings.pos_currently_open')}</StatisticLabel>
                  </Statistic>
              </CardContent>

              <CardContent extra>
                  <div
                    css={css`
                        display: flex;
                        justify-content: center;
                        align-items: flex-end;
                    `}>
                      <Icon size="big" color="teal" name="calendar check outline"/>
                      <CardHeader>{t('openings.view_current_openings')}</CardHeader>
                  </div>
              </CardContent>
          </Card>
      </CardGroup>
    )
}

export { OpeningActivity }
